import { Fragment, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Button } from "@mantine/core";
import { FiUpload } from "react-icons/fi";
import { useTheme } from "../../../ThemeContext";
import ProjectName from "../../Dropdowns/ProjectNames";
import { addDangerPrediction, updateDangerPrediction } from "../../../api/api"; // Assuming the API methods are imported

const VibrationSimulationForm = ({ isOpen, onClose, editData, handleUpdate }) => {
  // console.log("Edit data",editData)
  const { theme } = useTheme();
  const isDark = theme === "dark";
  // Check for editData._id to properly determine if we're editing
  const isEditing = editData && editData._id;
  const [pname, setPname] = useState(isEditing && editData.project_id ? editData.project_id : "");
  const [check, setCheck] = useState('');
  const [formValues, setFormValues] = useState({
    siteEngineerName: isEditing ? editData.siteEngineerName : "",
    inspectorName: isEditing ? editData.inspectorName : "",
    vibrationSimulation: isEditing ? editData.vibrationSimulation : false,
    wavePropagationSimulation: isEditing ? editData.wavePropagationSimulation : false,
    liquefactionAnalysis: isEditing ? editData.liquefactionAnalysis : false,
    comments: isEditing ? editData.comments : "",
    checklist: isEditing ? editData.checklist : null,
  });
  const [loading, setLoading] = useState(false);

  // Handle form input change
  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === "checkbox") {
      setFormValues({ ...formValues, [name]: checked });
    } else {
      setFormValues({ ...formValues, [name]: value });
    }
  };

  // Function to handle form submission
  const handleSubmit = async (e) => {
    console.log("button clicked");  
    e.preventDefault();

    // Create FormData object to send the data including the file
    const formData = new FormData();
    // if (editData) {
    //   console.log(editData)
    //   formData.append("id", editData._id);
    // }
    // Get the project ID - handle both string and object formats
    const projectId = typeof pname === 'object' ? pname._id : pname;
    formData.append("projectId", projectId);
    formData.append("siteEngineerName", formValues.siteEngineerName);
    formData.append("inspectorName", formValues.inspectorName);
    formData.append("vibrationSimulation", formValues.vibrationSimulation);
    formData.append("wavePropagation", formValues.wavePropagationSimulation);
    formData.append("liquefactionAnalysis", formValues.liquefactionAnalysis);
    formData.append("comment", formValues.comments);
    formData.append("checkListFile", check);
  //  await addDangerPrediction(formData);
  // console.log("Prediction added:", formData);
    // Print values of formData for debugging
    for (let pair of formData.entries()) {
      console.log(pair[0] + ": " + pair[1]);
    }
 

    try {
      // Check if we're in edit mode and have a valid ID
      const isEditMode = editData && editData._id;
      let response;
      
      if (isEditMode) {
        response = await updateDangerPrediction(editData._id, formData);
      } else {
        response = await addDangerPrediction(formData);
        console.log(response);
      }
    
          if (response) {
            onClose();
          } else {
            console.error("Error: Unable to submit form");
          }
        } catch (error) {
          console.error("Error submitting form:", error);
        } finally {
          handleUpdate()
          setLoading(false);
        }
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                className={`relative transform overflow-hidden rounded-md ${isDark ? "bg-gray-800 text-white" : "bg-white text-gray-900"} text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg`}
              >
                <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="text-center font-semibold leading-6">
                    <h3 className="text-lg py-4">Vibration Simulation Form</h3>
                  </div>
                  <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 mt-4">
                      <div>
                        <ProjectName pname={pname} handleChange={e => setPname(e.target.value)} />
                      </div>

                      <div>
                        <label className="block mb-2 text-sm font-medium">
                          Site Engineer Name
                        </label>
                        <input
                          name="siteEngineerName"
                          value={formValues.siteEngineerName}
                          onChange={handleChange}
                          className={`bg-gray-50 border border-gray-300 text-sm rounded-md block w-full p-2 ${isDark ? "bg-gray-700 text-white border-gray-600" : ""}`}
                          placeholder="Enter site engineer name"
                        />
                      </div>

                      <div>
                        <label className="block mb-2 text-sm font-medium">
                          Inspector Name
                        </label>
                        <input
                          name="inspectorName"
                          value={formValues.inspectorName}
                          onChange={handleChange}
                          className={`bg-gray-50 border border-gray-300 text-sm rounded-md block w-full p-2 ${isDark ? "bg-gray-700 text-white border-gray-600" : ""}`}
                          placeholder="Enter inspector name"
                        />
                      </div>

                      {/* Vibration Simulation */}
                      <div className="flex gap-2 flex-row-reverse">
                        <label className="block mb-2 text-sm font-medium">Vibration Simulation</label>
                        <input
                          type="checkbox"
                          name="vibrationSimulation"
                          checked={formValues.vibrationSimulation}
                          onChange={handleChange}
                          className={`rounded-md ${isDark ? "bg-gray-700 text-white" : ""}`}
                        />
                      </div>

                      {/* Wave Propagation Simulation */}
                      <div className="flex gap-2 flex-row-reverse">
                        <label className="inline text-sm font-medium">Wave Propagation Simulation</label>
                        <input
                          type="checkbox"
                          name="wavePropagationSimulation"
                          checked={formValues.wavePropagationSimulation}
                          onChange={handleChange}
                          className={`rounded-md ${isDark ? "bg-gray-700 text-white" : ""}`}
                        />
                      </div>

                      {/* Liquefaction Analysis */}
                      <div className="flex gap-2 flex-row-reverse">
                        <label className="inline text-sm font-medium">Liquefaction Analysis</label>
                        <input
                          type="checkbox"
                          name="liquefactionAnalysis"
                          checked={formValues.liquefactionAnalysis}
                          onChange={handleChange}
                          className={`rounded-md ${isDark ? "bg-gray-700 text-white" : ""}`}
                        />
                      </div>

                      {/* Checklist Upload */}
                      <div className="col-span-2">
                        <label className="block mb-2 text-sm font-medium">Checklist</label>
                        <div
                          className={`flex items-center justify-center border ${isDark ? "bg-gray-700 border-gray-600" : "bg-gray-50 border-gray-300"} rounded-md p-2 cursor-pointer`}
                          onClick={() => document.getElementById("file-upload").click()}
                        >
                          <FiUpload className="mr-2 text-lg" />
                          <span className="truncate">
                            {formValues.checklist
                              ? formValues.checklist.name.length > 20
                                ? `${formValues.checklist.name.slice(0, 17)}...`
                                : formValues.checklist.name
                              : "Upload Checklist"}
                          </span>
                          <input
                            type="file"
                            id="file-upload"
                            style={{ display: "none" }}
                            onChange={(e) => setCheck(e.target.files[0])}
                            accept=".xlsx, .xls"
                            name="checklist"
                          />
                        </div>
                      </div>

                      {/* Comments */}
                      <div className="col-span-2">
                        <label className="block mb-2 text-sm font-medium">Comments</label>
                        <textarea
                          name="comments"
                          value={formValues.comments}
                          onChange={handleChange}
                          className={`bg-gray-50 border border-gray-300 text-sm rounded-md block w-full p-2 ${isDark ? "bg-gray-700 text-white border-gray-600" : ""}`}
                          placeholder="Any additional comments"
                        />
                      </div>
                    </div>

                    <div className="flex justify-between mt-4 space-x-4">
                      <Button variant="outline" color="gray" onClick={onClose}>
                        Cancel
                      </Button>
                      <Button type="submit" color="blue">
                        Submit
                      </Button>
                    </div>
                  </form>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default VibrationSimulationForm;
